import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, AuthState } from '../services/authService';
import { DateTimeProvider } from './DateTimeProvider';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  tenantId: string | null;
  tenantName: string | null;
  login: () => Promise<void>;
  loginWithEmail: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  portalType: 'super-admin' | 'tenant-admin' | 'user';
  tenantSlug?: string;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ 
  children, 
  portalType,
  tenantSlug 
}) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    token: null,
    refreshToken: null,
  });
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = authService.subscribe(() => {
      const state = authService.getState();
      setAuthState(state);
      setTenantId(state.user?.tenantId || null);
      setTenantName(state.user?.tenantName || null);
    });
    initializeAuth();
    return unsubscribe;
  }, [portalType, tenantSlug]);

  const initializeAuth = async () => {
    try {
      await authService.initialize(portalType, tenantSlug);
      const state = authService.getState();
      setAuthState(state);
      
      if (state.user?.tenantId) {
        setTenantId(state.user.tenantId);
        setTenantName(state.user.tenantName || null);
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const login = async () => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.loginWithRedirect();
    } catch (error) {
      console.error('Login failed:', error);
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const loginWithEmail = async (email: string) => {
    try {
      await authService.loginWithMagicLink(email);
    } catch (error) {
      console.error('Magic link login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    await authService.logout();
    setAuthState({
      user: null,
      isLoading: false,
      isAuthenticated: false,
      token: null,
      refreshToken: null,
    });
    setTenantId(null);
    setTenantName(null);
  };

  const hasPermission = (permission: string): boolean => {
    if (!authState.user) return false;
    return authState.user.permissions?.includes(permission) ?? false;
  };

  const hasRole = (role: string): boolean => {
    if (!authState.user) return false;
    return authState.user.roles?.includes(role) ?? false;
  };

  return (
    <AuthContext.Provider
      value={{
        user: authState.user,
        isLoading: authState.isLoading,
        isAuthenticated: authState.isAuthenticated,
        tenantId,
        tenantName,
        login,
        loginWithEmail,
        logout,
        hasPermission,
        hasRole,
      }}
    >
      <DateTimeProvider>{children}</DateTimeProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const RequireAuth: React.FC<{ 
  children: ReactNode; 
  requiredPermissions?: string[];
  requiredRoles?: string[];
  fallback?: ReactNode;
}> = ({ children, requiredPermissions, requiredRoles, fallback }) => {
  const { isAuthenticated, isLoading, hasPermission, hasRole } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback || <LoginPage />;
  }

  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((role) => hasRole(role));
    if (!hasRequiredRole) {
      return <AccessDenied />;
    }
  }

  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasRequiredPermission = requiredPermissions.some((perm) => hasPermission(perm));
    if (!hasRequiredPermission) {
      return <AccessDenied />;
    }
  }

  return <>{children}</>;
};

export const LoginPage: React.FC = () => {
  const { login, loginWithEmail, isLoading, tenantName } = useAuth();
  const isSuperAdminLogin = window.location.pathname.startsWith('/admin');
  const rememberFlagKey = isSuperAdminLogin ? 'zurvit-sa-remember-me' : 'zurvit-auth-remember-me';
  const rememberEmailKey = isSuperAdminLogin ? 'zurvit-sa-remembered-email' : 'zurvit-auth-remembered-email';
  const [email, setEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem(rememberFlagKey) === 'true');
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState('');
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(rememberFlagKey) === 'true') {
      setEmail(localStorage.getItem(rememberEmailKey) || '');
    }
  }, [rememberEmailKey, rememberFlagKey]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setMagicLinkError('');
    setIsSendingMagicLink(true);

    if (rememberMe) {
      localStorage.setItem(rememberFlagKey, 'true');
      localStorage.setItem(rememberEmailKey, email.trim().toLowerCase());
    } else {
      localStorage.removeItem(rememberFlagKey);
      localStorage.removeItem(rememberEmailKey);
    }

    try {
      await loginWithEmail(email);
      setMagicLinkSent(true);
    } catch (err: any) {
      setMagicLinkSent(false);
      setMagicLinkError(err?.message || 'Failed to send sign-in link. Please try again or use SSO login.');
    } finally {
      setIsSendingMagicLink(false);
    }
  };

  return (
    <div className={`login-page${isSuperAdminLogin ? ' super-admin-login' : ''}`}>
      <div className="login-container">
        <div className="login-header">
          <div className={`logo${isSuperAdminLogin ? ' super-admin-logo' : ''}`}>{isSuperAdminLogin ? 'Zurvit Admin' : 'Zurvit'}</div>
          {tenantName && <div className="tenant-name">{tenantName}</div>}
          <div className="login-subtitle">Sign in to your account</div>
        </div>

        <div className="login-content">
          {showMagicLink ? (
            magicLinkSent ? (
              <div className="magic-link-sent auth-card auth-card--center">
                <div className="icon">✉️</div>
                <h2>Check your email</h2>
                <p>We sent a sign-in link to {email}</p>
                <p className="hint">Click the link in the email to sign in.</p>
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    setShowMagicLink(false);
                    setMagicLinkSent(false);
                    setEmail('');
                  }}
                >
                  Use different email
                </button>
              </div>
            ) : (
              <form onSubmit={handleMagicLink}>
                <h2>Sign in with email</h2>
                <p>We'll send you a magic link to sign in.</p>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <label className="remember-me-row" htmlFor="remember-me-login">
                  <input
                    id="remember-me-login"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => {
                      setRememberMe(e.target.checked);
                      if (!e.target.checked) {
                        localStorage.removeItem(rememberFlagKey);
                        localStorage.removeItem(rememberEmailKey);
                      }
                    }}
                  />
                  Remember me
                </label>
                {magicLinkError && <p className="error-message">{magicLinkError}</p>}
                <button type="submit" className="btn-primary" disabled={isSendingMagicLink}>
                  {isSendingMagicLink ? 'Sending...' : 'Send Magic Link'}
                </button>
                <button 
                  type="button" 
                  className="btn-link"
                  onClick={() => {
                    setShowMagicLink(false);
                    setMagicLinkError('');
                  }}
                >
                  ← Back to SSO login
                </button>
              </form>
            )
          ) : (
            <>
              <button 
                className="btn-microsoft" 
                onClick={login}
                disabled={isLoading}
              >
                <svg viewBox="0 0 21 21" className="microsoft-icon">
                  <path fill="#f25022" d="M1 1h9v9H1z" />
                  <path fill="#00a4ef" d="M1 11h9v9H1z" />
                  <path fill="#7fba00" d="M11 1h9v9h-9z" />
                  <path fill="#ffb900" d="M11 11h9v9h-9z" />
                </svg>
                {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
              </button>

              <div className="divider">
                <span>or</span>
              </div>

              <button 
                className="btn-link"
                onClick={() => setShowMagicLink(true)}
              >
                Sign in with email instead
              </button>
            </>
          )}
        </div>

        <div className="login-footer">
          <p>Having trouble? Contact your administrator</p>
        </div>
      </div>
    </div>
  );
};

export const AccessDenied: React.FC = () => {
  const { logout, login } = useAuth();

  return (
    <div className="access-denied">
      <div className="container">
        <h1>Access Denied</h1>
        <p>You don't have permission to access this page.</p>
        <p>This incident has been logged.</p>
        <div className="actions">
          <button onClick={logout}>Sign out</button>
          <button onClick={login}>Try different account</button>
        </div>
      </div>
    </div>
  );
};

export const LoadingScreen: React.FC = () => (
  <div className="loading-screen">
    <div className="loading-spinner" />
    <p>Loading...</p>
  </div>
);

export default AuthContext;
